import { Box, Button, Typography } from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { ACTIONS, EventData, EVENTS, Joyride, STATUS, Step, TooltipRenderProps } from 'react-joyride';
import { useLocation, useNavigate } from 'react-router';
import arcLogo from '../../assets/images/arc-logo-white.svg';
import { useUserApi } from '../../services/api/useUser.api.ts';
import { TourContext } from './TourContext.tsx';

const dashboardSteps: Step[] = [
  {
    target: 'body',
    placement: 'center',
    content: (
      <Box sx={{ width: 800 }}>
        <Box
          sx={{
            borderTopRightRadius: 12,
            borderTopLeftRadius: 12,
            padding: 15,
            background: 'linear-gradient(95deg, #20567E 27.72%, #132B50 91.52%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <img src={arcLogo} alt='ARC Tool Logo' width={300} />
        </Box>
        <Box sx={{ padding: 4 }}>
          <Typography
            variant='h4'
            sx={{ fontWeight: 800, textAlign: 'center', color: 'primary.main', fontSize: '36px' }}
          >
            Welcome to ARC Tool
          </Typography>
          <Typography variant='body1' sx={{ textAlign: 'center', marginTop: 2 }}>
            {"We are excited you're here. Let's take a tour through"}
            <br /> our product.
          </Typography>
        </Box>
      </Box>
    ),
    skipBeacon: true,
    spotlightPadding: 0,
  },
  {
    target: '#tour-saved-searches-section',
    content: (
      <Box style={{ maxWidth: 400, padding: 4, color: '#fff' }}>
        <Typography
          variant='body1'
          sx={{
            fontWeight: 600,
          }}
        >
          Previous cases are stored here
        </Typography>
        <Typography variant='body2' sx={{ marginTop: 2 }}>
          Pick up previous work where you left off by selecting from the list of cases here.
        </Typography>
      </Box>
    ),
    skipScroll: true,
  },
  {
    target: '#tour-new-case-button',
    content: (
      <Box style={{ maxWidth: 400, padding: 4, color: '#fff' }}>
        <Typography
          variant='body1'
          sx={{
            fontWeight: 600,
          }}
        >
          Click here to start a new case
        </Typography>
        <Typography variant='body2' sx={{ marginTop: 2 }}>
          A case is where you can identify, specify, and store evidence of disparities related to a particular set of
          facts or details.
        </Typography>
      </Box>
    ),
    skipScroll: true,
  },
];

const caseDetailsSteps: Step[] = [
  {
    target: '#tour-search-panel',
    content: (
      <Box style={{ maxWidth: 400, padding: 4, color: '#fff' }}>
        <Typography
          variant='body1'
          sx={{
            fontWeight: 600,
          }}
        >
          The details define the case
        </Typography>
        <Typography variant='body2' sx={{ marginTop: 2 }}>
          The facts of the case that are relevant to identifying racial disparities are entered here. This list will
          grow as the ability of our tool to identify more types of disparities.
        </Typography>
      </Box>
    ),
    placement: 'right',
  },
  {
    target: '#tour-disparity-panel',
    content: (
      <Box style={{ maxWidth: 400, padding: 4, color: '#fff' }}>
        <Typography
          variant='body1'
          sx={{
            fontWeight: 600,
          }}
        >
          Disparities at a glance
        </Typography>
        <Typography variant='body2' sx={{ marginTop: 2 }}>
          This collection of cards represents a series of various disparities that are related to the details you
          entered. Each card displays a measure of the disparity related to a particular aspect of this case.
        </Typography>
      </Box>
    ),
    placement: 'right',
  },
  {
    target: '#tour-disparity-panel [data-tour-disparity-item]:first-of-type',
    content: (
      <Box style={{ maxWidth: 400, padding: 4, color: '#fff' }}>
        <Typography
          variant='body1'
          sx={{
            fontWeight: 600,
          }}
        >
          Find out more by selecting
        </Typography>
        <Typography variant='body2' sx={{ marginTop: 2 }}>
          Details of an individual disparity can be displayed by selecting the card. If this disparity is of interest to
          you, use the select box to tag it.
        </Typography>
      </Box>
    ),
    placement: 'right',
    skipScroll: true,
  },
  {
    target: '#tour-new-disparity-search-button',
    content: (
      <Box style={{ maxWidth: 400, padding: 4, color: '#fff' }}>
        <Typography
          variant='body1'
          sx={{
            fontWeight: 600,
          }}
        >
          Define a custom disparity search
        </Typography>
        <Typography variant='body2' sx={{ marginTop: 2 }}>
          If none of the recommended disparities match what you are looking for, you can define your own disparity here.
        </Typography>
      </Box>
    ),
  },
  {
    target: '#tour-disparity-details',
    content: (
      <Box style={{ maxWidth: 400, padding: 4, color: '#fff' }}>
        <Typography
          variant='body1'
          sx={{
            fontWeight: 600,
          }}
        >
          Dive deeper into a disparity
        </Typography>
        <Typography variant='body2' sx={{ marginTop: 2 }}>
          Clicking into a disparity will pull up more details, describing what the disparity measures, how to interpret
          it, and a visual representation of the size of the disparity.
        </Typography>
      </Box>
    ),
    placement: 'left',
  },
  {
    target: '#tour-comparator-button button',
    content: (
      <Box style={{ maxWidth: 400, padding: 4, color: '#fff' }}>
        <Typography
          variant='body1'
          sx={{
            fontWeight: 600,
          }}
        >
          Tailor your comparison with other cases
        </Typography>
        <Typography variant='body2' sx={{ marginTop: 2 }}>
          Want to compare with other cases that include similar behavior? Use the Comparison Cases feature to customize
          the comparison pool of cases by statutes.
        </Typography>
      </Box>
    ),
    skipScroll: true,
  },
  {
    target: '#tour-download-button',
    content: (
      <Box style={{ maxWidth: 400, padding: 4, color: '#fff' }}>
        <Typography
          variant='body1'
          sx={{
            fontWeight: 600,
          }}
        >
          Share your work with others
        </Typography>
        <Typography variant='body2' sx={{ marginTop: 2 }}>
          Once you’ve saved the disparities that you want to act upon, generate a report or export the data for
          additional analysis.
        </Typography>
      </Box>
    ),
  },
  {
    target: '#tour-back-button',
    content: (
      <Box style={{ maxWidth: 400, padding: 4, color: '#fff' }}>
        <Typography
          variant='body1'
          sx={{
            fontWeight: 600,
          }}
        >
          Back to the dashboard
        </Typography>
        <Typography variant='body2' sx={{ marginTop: 2 }}>
          Use this button to navigate back and see all your other cases on your home page.
        </Typography>
      </Box>
    ),
  },
  {
    target: 'body',
    placement: 'center',
    content: (
      <Box style={{ maxWidth: 400, padding: 4, color: '#fff' }}>
        <Typography
          variant='body1'
          sx={{
            fontWeight: 600,
          }}
        >
          ARC Tool
        </Typography>
        <Typography variant='body2' sx={{ marginTop: 2 }}>
          This concludes the tour of the ARC Tool. We hope you find it useful in identifying and addressing racial
          disparities. Click finish to exit the tour and create your first case.
        </Typography>
      </Box>
    ),
  },
];

const CustomTooltipWrapper = (props: TooltipRenderProps) => {
  const isFirstStep = props.index === 0;
  const isLastStep = props.index === props.size - 1;

  return (
    <Box
      {...props.tooltipProps}
      sx={{
        borderRadius: 2,
        backgroundColor: isFirstStep ? '#fff' : 'accentBlue.main',
      }}
    >
      <Box sx={{ padding: isFirstStep ? 0 : 4 }}>{props.step.content}</Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: 4 }}>
        {props.skipProps && (
          <Button sx={{ color: isFirstStep ? 'accentBlue.main' : '#fff', fontSize: '12px' }} {...props.skipProps}>
            Hide these tips
          </Button>
        )}
        <Button
          disableElevation
          {...(isLastStep ? props.closeProps : props.primaryProps)}
          variant='contained'
          color={isFirstStep ? 'primary' : 'inherit'}
          sx={{
            ...(isFirstStep ? {} : { backgroundColor: '#fff', color: 'primary.main' }),
          }}
        >
          {isFirstStep ? "Let's go" : isLastStep ? 'Finish' : 'Next'}
        </Button>
      </Box>
    </Box>
  );
};

export const TourProvider = ({ children }: { children: ReactNode }) => {
  type TourRunState = { type: 'auto' } | { type: 'running'; stepIndex: number } | { type: 'stopped' };
  const [tourRunState, setTourRunState] = useState<TourRunState>({ type: 'auto' });

  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { getUserInfo, updateUser, getUserCases } = useUserApi();

  const { data: user, isLoading: userLoading } = useQuery({
    queryKey: ['userInfo'],
    queryFn: getUserInfo,
  });

  const { data: userCases } = useQuery({
    queryKey: ['userCases', user?.id],
    queryFn: () => getUserCases(user!.id, { page: 1, per_page: 20, sort: 'updated_at:desc', query: '' }),
    enabled: !!user?.id && !user?.tour_completed && location.pathname === '/',
  });
  const recentUserCase = userCases?.data?.[0]?.id;

  const steps = useMemo<Step[]>(() => {
    if (!user) return [];
    return [
      dashboardSteps[0],
      dashboardSteps[1],
      {
        ...dashboardSteps[2],
        data: { next: recentUserCase ? `/case-details/${recentUserCase}` : '/case-details' },
      },
      ...caseDetailsSteps,
    ];
  }, [user, recentUserCase]);

  const { mutate: updateUserMutation } = useMutation({
    mutationKey: ['completeTour'],
    mutationFn: ({ userId, tour_completed }: { userId: number; tour_completed: boolean }) =>
      updateUser(userId, { tour_completed }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['userInfo'], exact: false });
    },
  });

  // Derive isRunning and stepIndex during render — no effect needed
  const isTourPath = location.pathname === '/' || location.pathname.startsWith('/case-details');
  const autoShouldRun = isTourPath && !userLoading && !!user && !user.tour_completed;

  const isRunning = tourRunState.type === 'running' ? true : tourRunState.type === 'stopped' ? false : autoShouldRun;

  const stepIndex =
    tourRunState.type === 'running' ? tourRunState.stepIndex : location.pathname.startsWith('/case-details') ? 3 : 0;

  useEffect(() => {
    if (!isRunning) return;
    const { style } = document.documentElement;
    const previousOverflow = style.overflow;
    style.overflow = 'hidden';
    return () => {
      style.overflow = previousOverflow;
    };
  }, [isRunning]);

  const stopTour = useCallback(() => {
    setTourRunState({ type: 'stopped' });
  }, []);

  const startTour = useCallback(() => {
    setTourRunState({ type: 'running', stepIndex: 0 });
  }, []);

  const handleJoyrideCallback = (data: EventData) => {
    const { action, index, status, type, step } = data;

    if (status === STATUS.FINISHED || status === STATUS.SKIPPED) {
      setTourRunState({ type: 'stopped' });
      updateUserMutation({ userId: user!.id, tour_completed: true });
      setTimeout(() => navigate('/'), 800);
      return;
    }

    if (type === EVENTS.STEP_AFTER) {
      const isPrev = action === ACTIONS.PREV;
      const navPath = step?.data ? (isPrev ? step.data.previous : step.data.next) : undefined;
      const safeNextIndex = Math.max(0, index + (isPrev ? -1 : 1));

      if (navPath) navigate(navPath);

      const targetSelector = steps[safeNextIndex]?.target as string | undefined;
      if (targetSelector && ['#tour-disparity-details', '#tour-comparator-button button'].includes(targetSelector)) {
        const firstItem = document.querySelector('#tour-disparity-panel [data-tour-disparity-item]:first-of-type');
        if (firstItem) (firstItem as HTMLElement).click();
      }

      setTourRunState({ type: 'running', stepIndex: safeNextIndex });
      return;
    }

    if (type === EVENTS.TARGET_NOT_FOUND) {
      setTimeout(() => setTourRunState((prev) => ({ ...prev })), 250);
    }
  };

  return (
    <TourContext.Provider value={{ startTour, stopTour, isRunning }}>
      {children}
      <Joyride
        steps={steps}
        run={isRunning}
        stepIndex={stepIndex}
        continuous={true}
        scrollToFirstStep
        tooltipComponent={CustomTooltipWrapper}
        onEvent={handleJoyrideCallback}
        options={{
          skipBeacon: true,
          blockTargetInteraction: true,
          showProgress: true,
          skipScroll: true,
          dismissKeyAction: false,
          overlayClickAction: false,
          primaryColor: '#20567E',
          arrowColor: '#20567E',
          zIndex: 10000,
        }}
      />
    </TourContext.Provider>
  );
};
